/* Elternportal, teacher side: one private link per parent email.
   Plume (the notify function) creates the links and emails the invitations; the app shows
   who was invited and when they last looked, and can resend, renew or block a link. */
import { CLOUD, loggedIn, sbRefresh, sbRest, sbUrl } from './api.js';
import { PARENT_IC, hasAddon, openAddonSheet } from './addons.js';
import { fmtDate, today } from '../dates.js';
import { t } from '../i18n/index.js';
import { stu } from '../model.js';
import { IC, ask, esc, toast } from '../ui.js';

const LINKS=new Map();          // studentId → [{id,token,email,sent_at,last_seen_at}]
export const parentEmailsOf=s=>[...new Set([s&&s.parentEmail,s&&s.parentEmail2].map(x=>String(x||'').trim().toLowerCase()).filter(x=>/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x)))];
const linkUrl=l=>location.origin+'/p/'+l.token;

async function fn(path,body){
  if(Date.now()>CLOUD.auth.exp)await sbRefresh();
  const r=await fetch(sbUrl('/functions/v1/notify/'+path),{method:'POST',
    headers:{apikey:CLOUD.cfg.key,Authorization:'Bearer '+CLOUD.auth.at,'Content-Type':'application/json'},body:JSON.stringify(body||{})});
  const d=await r.json().catch(()=>({}));
  if(!r.ok)throw new Error(d.error||('HTTP '+r.status));
  return d;
}

export const parentAction=(id,action)=>fn('parents/action',{id,action});

/* make sure every parent email has a link and new ones get their invitation; at most every 20 s */
let lastSync=0,syncing=null;
export function syncParents(force){
  if(!loggedIn()||!hasAddon('parents'))return Promise.resolve(null);
  if(syncing)return syncing;
  if(!force&&Date.now()-lastSync<20000)return Promise.resolve(null);
  lastSync=Date.now();
  syncing=fn('parents/sync').then(d=>{LINKS.clear();refreshCard();if(d&&(d.created||d.sent||d.revoked))import('../views/parents.js').then(m=>{m.invalidateParents();if(document.documentElement.dataset.view==='parents')import('../ui.js').then(u=>u.render())});return d}).catch(e=>{console.warn('parents sync',e);return null}).finally(()=>{syncing=null});
  return syncing;
}
addEventListener('plume:parents',()=>syncParents(true));

async function loadLinks(stuId){
  const r=await sbRest('parent_links?select=id,token,email,sent_at,last_seen_at&revoked_at=is.null&student_id=eq.'+encodeURIComponent(stuId)+'&order=created_at');
  const rows=r.ok?await r.json():[];
  LINKS.set(stuId,rows);return rows;
}

const when=iso=>{if(!iso)return '';const d=new Date(iso),ds=d.toISOString().slice(0,10);
  return ds===today()?t('pl.today'):fmtDate(ds)};

/* the card on the student page; the rows load afterwards */
export function parentCard(s){
  if(!loggedIn()||s._org&&!hasAddon('parents'))return '';
  if(!hasAddon('parents'))return `<div class="card mb parentcard teaser"><div class="cardhead"><h2>${t('par.title')}</h2><span class="chip">${t('addon.chip')}</span></div>
    <p class="muted mb8">${t('par.teaser')}</p><button class="btn small" data-plo="addon">${PARENT_IC} ${t('addon.more')}</button></div>`;
  return `<div class="card mb parentcard" data-pcard="${esc(s.id)}"><div class="cardhead"><h2>${t('par.title')}</h2></div>
    <div class="plinks">${rows(s)}</div></div>`;
}
function rows(s){
  const emails=parentEmailsOf(s),links=LINKS.get(s.id);
  if(!emails.length&&!(links&&links.length))return `<p class="muted mb8">${t('pl.noEmail')}</p><button class="btn small" data-plo="edit">${IC.edit} ${t('pl.addEmail')}</button>`;
  if(!links)return `<p class="muted">${t('pl.loading')}</p>`;
  const out=[];
  for(const l of links){
    const status=[l.sent_at?t('pl.sent',{d:when(l.sent_at)}):t('pl.notSent'),l.last_seen_at?t('pl.seen',{d:when(l.last_seen_at)}):l.sent_at?t('pl.notSeen'):''].filter(Boolean).join(', ');
    out.push(`<div class="plrow"><span class="pl-ic${l.last_seen_at?' on':''}">${PARENT_IC}</span><div class="grow"><b>${esc(l.email||t('pl.legacy'))}</b><small>${esc(status)}</small></div>
      <div class="pl-acts">
        <button class="btn small ghost" data-plcopy="${esc(l.id)}" title="${esc(t('par.copy'))}">${t('par.copy')}</button>
        ${l.email?`<button class="btn small${l.sent_at?' ghost':' primary'}" data-plact="send" data-pl="${esc(l.id)}">${l.sent_at?t('pl.resend'):t('pl.send')}</button>`:''}
        <button class="btn small ghost" data-plact="renew" data-pl="${esc(l.id)}">${t('par.renew')}</button>
        <button class="btn small ghost" data-plact="revoke" data-pl="${esc(l.id)}">${t('par.off')}</button>
      </div></div>`);
  }
  for(const e of emails)if(!links.some(l=>(l.email||'').toLowerCase()===e))
    out.push(`<div class="plrow"><span class="pl-ic">${PARENT_IC}</span><div class="grow"><b>${esc(e)}</b><small>${t('pl.setup')}</small></div></div>`);
  return out.join('')+`<p class="hint mt8">${t('pl.hint')}</p>`;
}
function refreshCard(){
  const box=document.querySelector('[data-pcard]');if(!box)return;
  const s=stu(box.dataset.pcard);if(!s)return;
  const fill=()=>{const el=box.querySelector('.plinks');if(el){el.innerHTML=rows(s);wire(box,s)}};
  if(LINKS.has(s.id))fill();else loadLinks(s.id).then(fill).catch(()=>{});
}

export function bindParentCard(m,openEditor){
  m.querySelectorAll('[data-plo="addon"]').forEach(b=>b.onclick=()=>openAddonSheet('parents'));
  const box=m.querySelector('[data-pcard]');if(!box)return;
  const s=stu(box.dataset.pcard);if(!s)return;
  box._edit=openEditor;
  if(!LINKS.has(s.id))loadLinks(s.id).then(rows0=>{
    // an email without a link yet: let Plume set it up now
    if(parentEmailsOf(s).some(e=>!rows0.some(l=>(l.email||'').toLowerCase()===e)))syncParents();
    refreshCard();
  }).catch(()=>{});
  wire(box,s);
}
function wire(box,s){
  box.querySelectorAll('[data-plo="edit"]').forEach(b=>b.onclick=()=>box._edit&&box._edit(s.id));
  box.querySelectorAll('[data-plcopy]').forEach(b=>b.onclick=()=>{
    const l=(LINKS.get(s.id)||[]).find(x=>x.id===b.dataset.plcopy);if(!l)return;const u=linkUrl(l);
    if(navigator.clipboard)navigator.clipboard.writeText(u).then(()=>toast(t('par.copied')),()=>prompt('',u));else prompt('',u);
  });
  box.querySelectorAll('[data-plact]').forEach(b=>b.onclick=()=>{
    const a=b.dataset.plact,id=b.dataset.pl;
    const run=async()=>{b.disabled=true;
      try{const d=await fn('parents/action',{id,action:a});
        toast(a==='revoke'?t('par.offDone'):d.sent?t('pl.sentNow'):t('pl.renewed'));
        await loadLinks(s.id);refreshCard();
      }catch(e){console.error(e);toast(t('plan.err'));b.disabled=false}};
    if(a==='revoke')ask(t('pl.revokeAsk'),run,true);else if(a==='renew')ask(t('pl.renewAsk'),run,true);else run();
  });
}
